/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { createSelector } from '@reduxjs/toolkit';
import { Category, CategoryType } from './Categories';
import { getDateDiff } from './common';
import { LicenseData } from './LicenseModel';

const colors: string[] = ['#CFE1EE', '#DFE9D6', '#FFF2CC', '#FFE8CC', '#F4D1D3'];

/** Users shown per bar-chart page — same chart, paged horizontally through all users. */
export const LICENSE_BAR_CHART_PAGE_SIZE = 50;

export function sliceBarChartPage(chartData: any, page: number, pageSize = LICENSE_BAR_CHART_PAGE_SIZE) {
    if (!chartData?.xaxisData?.length) {
        return chartData;
    }
    const start = page * pageSize;
    const end = start + pageSize;
    const xaxisData = chartData.xaxisData.slice(start, end);
    const datasets = chartData.datasets.map((ds: any) => ({
        ...ds,
        label: ds.label.slice(start, end),
        data: ds.data.slice(start, end),
        backgroundColor: ds.backgroundColor.slice(start, end),
        borderColor: ds.borderColor.slice(start, end),
        hoverBackgroundColor: ds.hoverBackgroundColor.slice(start, end),
    }));
    return { ...chartData, xaxisData, datasets };
}

function groupByKey<T>(array: T[], keyFn: (item: T) => string): T[][] {
    const groups = new Map<string, T[]>();
    array.forEach((item) => {
        const key = keyFn(item);
        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key)!.push(item);
    });
    return Array.from(groups.values());
}

export function buildLicenseChartData(
    data: LicenseData[] | undefined,
    selectedCategory: Category | undefined,
    isLoading: boolean
) {
    if (
        data === undefined ||
        selectedCategory === undefined ||
        (data.length === undefined && selectedCategory.id !== 5) ||
        isLoading === true
    ) {
        return undefined;
    }

    if (selectedCategory.type !== CategoryType.License) {
        return undefined;
    }

    if (data.length === 0) {
        return {
            xaxisData: [],
            yaxisData: [],
            doughnutLabel: [],
            doughnutData: [],
            groupedDoughnutLabel: [],
            groupedDoughnutData: [{ data: [], backgroundColor: [], labels: [], hoverBackgroundColor: '#ffbe00', hoverBorderColor: '#ffbe00' }],
            doughnutImageNames: [],
            datasets: [],
            maxBarnum: 1,
            licenseTypeTexts: []
        };
    }

    const result = groupByKey(data, (item: LicenseData) => {
        if (item.user != null) {
            return `${item.user.firstname}|${item.user.lastname}`;
        }
        if (item.company != null) {
            return item.company.companyname || '';
        }
        return '';
    });

    const xaxislabels: string[] = [];
    let maxLength = 1;
    result.forEach((licenseByUsers) => {
        xaxislabels.push(
            licenseByUsers[0].user != null
                ? `${licenseByUsers[0].user.firstname} ${licenseByUsers[0].user.lastname}`
                : licenseByUsers[0].company != null ? licenseByUsers[0].company.companyname : ''
        );
        maxLength = licenseByUsers.length > maxLength ? licenseByUsers.length : maxLength;
    });
    maxLength = maxLength > 10 ? 10 : maxLength;

    const dataSets = [];
    for (let j = 0; j < maxLength; j++) {
        const dataSet = {
            label: [] as string[],
            data: [] as number[],
            backgroundColor: [] as string[],
            borderColor: [] as string[],
            hoverBackgroundColor: [] as string[],
        };

        for (let i = 0; i < xaxislabels.length; i++) {
            if (result[i][j] !== undefined && result[i][j].subscription !== undefined) {
                const color = colors[j % colors.length];
                dataSet.label.push(result[i][j].machinename);
                const expiresin = getDateDiff(
                    new Date(),
                    result[i][j].subscription.length !== 0
                        ? new Date(result[i][j].subscription[0].enddate + 'Z')
                        : new Date()
                );
                if (expiresin < 0) {
                    dataSet.backgroundColor.push('#bfbfc6');
                    dataSet.borderColor.push('#bfbfc6');
                    dataSet.hoverBackgroundColor.push('#bfbfc6');
                } else if (expiresin < 7) {
                    dataSet.backgroundColor.push('#ff8b00');
                    dataSet.borderColor.push('#ff8b00');
                    dataSet.hoverBackgroundColor.push('#ff8b00');
                } else {
                    dataSet.backgroundColor.push(color);
                    dataSet.borderColor.push(color);
                    dataSet.hoverBackgroundColor.push(color);
                }
                dataSet.data.push(Math.abs(expiresin));
            } else {
                dataSet.label.push(' ');
                dataSet.data.push(0);
                dataSet.backgroundColor.push('#bfbfc6');
                dataSet.borderColor.push('#bfbfc6');
                dataSet.hoverBackgroundColor.push('#bfbfc6');
            }
        }
        dataSets.push(dataSet);
    }

    const companyNames: string[] = [];
    const companyLicenseDataSet: number[] = [];
    const companyLicenseColors: string[] = [];
    const licenseTypeTexts: string[] = [];
    const licenseByCompanies = groupByKey(data, (item: LicenseData) => {
        if (item.user != null && item.user.company != null) {
            return item.user.company.companyname;
        }
        if (item.company != null) {
            return item.company.companyname;
        }
        return 'Internal Trimble';
    });

    licenseByCompanies.forEach((licenseByCompany, index) => {
        companyNames.push(
            (licenseByCompany[0].user != null && licenseByCompany[0].user.company != null)
                ? licenseByCompany[0].user.company.companyname
                : (licenseByCompany[0].company != null)
                    ? licenseByCompany[0].company.companyname
                    : 'Internal Trimble'
        );
        companyLicenseDataSet.push(licenseByCompany.length);
        companyLicenseColors.push(colors[index % colors.length]);

        const licenseByTypes = groupByKey(licenseByCompany, (item: LicenseData) =>
            item.licensetype != null ? item.licensetype.typename : ''
        );
        let licenseTypeText = '';
        licenseByTypes.forEach((licensebytype) => {
            licenseTypeText = `${licenseTypeText}${licensebytype.length} ${licensebytype[0].licensetype.typename}\n`;
        });
        licenseTypeTexts.push(licenseTypeText);
    });
    const doughnutData = [{
        data: companyLicenseDataSet,
        backgroundColor: companyLicenseColors,
        labels: companyNames,
        hoverBackgroundColor: '#ffbe00',
        hoverBorderColor: '#ffbe00'
    }];

    return {
        xaxisData: xaxislabels,
        yaxisData: ['dummy', 'dummy'],
        doughnutLabel: ['dummy', 'dummy'],
        doughnutData: ['dummy', 'dummy'],
        groupedDoughnutLabel: [],
        groupedDoughnutData: doughnutData,
        doughnutImageNames: ['dummy', 'dummy'],
        datasets: dataSets,
        maxBarnum: maxLength,
        licenseTypeTexts
    };
}

export const selectLicenseChartData = createSelector(
    [
        (state: any) => state.dashboard?.data,
        (state: any) => state.dashboard?.selectedCategory,
        (state: any) => state.dashboard?.isLoading,
    ],
    (data, selectedCategory, isLoading) =>
        buildLicenseChartData(data, selectedCategory, isLoading)
);
